/**
 * PATH: packages/ui/src/kanban/shortcuts/registry.ts
 * FILE_ID: UI-KANBAN-SHORTCUTS-REG-001
 * PURPOSE: The kanban shortcut registry — single source of truth for every
 *          board action's `{id, module, label, defaultKeys: {mac, win},
 *          scope, rebindable}` plus the per-operator override store
 *          (localStorage), conflict detection, and the key-canonicalization
 *          used by the keydown handler.
 * WHY: ARC-8 U16 (client notes p.1): "Add shortcut keyboards in
 *      comprehensive detail and allow to change shortcuts … by Windows /
 *      Mac". The `?` cheat sheet and the keydown switch previously each
 *      hardcoded their own key list — the registry kills the "(B)"
 *      tooltip-lies class of bug (UAT Stream 1) by making both render from
 *      one table.
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────┐   ┌───────────────────────┐
 *   │ KANBAN_      │ → │ overrides store  │ → │ resolveCanonicalKey / │
 *   │ SHORTCUTS    │   │ (localStorage)   │   │ cheat sheet rows      │
 *   └──────────────┘   └──────────────────┘   └───────────────────────┘
 * DEPENDENCIES: none (pure TS — unit-testable without React).
 * RELATED:
 *   - components/board-view/useBoardKeyboardShortcuts.ts: keydown consumer
 *   - components/board-view/BoardShortcutsSheet.tsx: cheat sheet + rebind UI
 */

export type ShortcutScope = 'board' | 'hovered-card' | 'hovered-lane' | 'typing-safe'

export interface ShortcutDef {
  /** Stable identifier — the override store keys off this. */
  id: string
  module: 'kanban'
  label: string
  /** Display strings per platform (single-key actions share one). */
  defaultKeys: { mac: string; win: string }
  /** The exact `KeyboardEvent.key` the keydown switch matches (single-key
   *  actions only; chord rows like undo leave this undefined). */
  matchKey?: string
  scope: ShortcutScope
  /** Only single-key, non-reserved actions may be rebound in v1. */
  rebindable: boolean
  /** Cheat-sheet / master-settings grouping (Navigation / Actions / Views).
   *  The hub-level shortcuts reference derives its kanban section from this
   *  table, so the grouping lives HERE, not in a parallel hardcoded list. */
  category: 'Navigation' | 'Actions' | 'Views'
}

/** Every kanban board shortcut, in cheat-sheet display order.
 *
 *  Rebindable widening (WP1, 2026-07-05 — operator: "I cannot change the
 *  shortcuts"): priority 1/2/3, pin, and the 4/5/6 view keys are now
 *  rebindable. The keydown consumer routes the hover-gated card hotkeys
 *  through resolveCanonicalKey the same way as the switch, so a rebound key
 *  drives BOTH meanings of a dual-semantics key (hovered-card priority AND
 *  view switch) — one binding, both behaviours, no drift. Shift-chords are
 *  supported natively: `e.key` already encodes shift ('G', '!'), so an
 *  override value is always a single KeyboardEvent.key string.
 */
export const KANBAN_SHORTCUTS: readonly ShortcutDef[] = [
  { id: 'focus-search', module: 'kanban', label: 'Focus search', defaultKeys: { mac: 'F', win: 'F' }, matchKey: 'f', scope: 'board', rebindable: true, category: 'Navigation' },
  { id: 'focus-search-alias', module: 'kanban', label: 'Focus search (alias)', defaultKeys: { mac: '/', win: '/' }, matchKey: '/', scope: 'board', rebindable: false, category: 'Navigation' },
  { id: 'toggle-filters', module: 'kanban', label: 'Toggle filters bar', defaultKeys: { mac: '⇧ F', win: 'Shift + F' }, matchKey: 'F', scope: 'board', rebindable: true, category: 'Actions' },
  { id: 'toggle-inbox', module: 'kanban', label: 'Toggle Inbox rail', defaultKeys: { mac: 'I', win: 'I' }, matchKey: 'i', scope: 'board', rebindable: true, category: 'Navigation' },
  { id: 'switch-boards', module: 'kanban', label: 'Switch boards', defaultKeys: { mac: 'B', win: 'B' }, matchKey: 'b', scope: 'board', rebindable: true, category: 'Navigation' },
  { id: 'new-card', module: 'kanban', label: 'Add card to hovered lane', defaultKeys: { mac: 'N', win: 'N' }, matchKey: 'n', scope: 'hovered-lane', rebindable: true, category: 'Actions' },
  { id: 'archive-or-tick', module: 'kanban', label: 'Archive hovered card (tick on checklist lane)', defaultKeys: { mac: 'C', win: 'C' }, matchKey: 'c', scope: 'hovered-card', rebindable: true, category: 'Actions' },
  { id: 'allocate-board', module: 'kanban', label: 'Allocate hovered card to another board', defaultKeys: { mac: 'A', win: 'A' }, matchKey: 'a', scope: 'hovered-card', rebindable: true, category: 'Actions' },
  { id: 'priority-normal', module: 'kanban', label: 'Hovered card → priority Normal · else Board view', defaultKeys: { mac: '1', win: '1' }, matchKey: '1', scope: 'hovered-card', rebindable: true, category: 'Views' },
  { id: 'priority-high', module: 'kanban', label: 'Hovered card → priority High · else Time view buckets', defaultKeys: { mac: '2', win: '2' }, matchKey: '2', scope: 'hovered-card', rebindable: true, category: 'Views' },
  { id: 'priority-urgent', module: 'kanban', label: 'Hovered card → priority Urgent · else Time view timeline', defaultKeys: { mac: '3', win: '3' }, matchKey: '3', scope: 'hovered-card', rebindable: true, category: 'Views' },
  { id: 'toggle-pin', module: 'kanban', label: 'Pin / unpin hovered card to lane top', defaultKeys: { mac: 'P', win: 'P' }, matchKey: 'p', scope: 'hovered-card', rebindable: true, category: 'Actions' },
  { id: 'view-calendar', module: 'kanban', label: 'Calendar view', defaultKeys: { mac: '4', win: '4' }, matchKey: '4', scope: 'board', rebindable: true, category: 'Views' },
  { id: 'view-table', module: 'kanban', label: 'Table view', defaultKeys: { mac: '5', win: '5' }, matchKey: '5', scope: 'board', rebindable: true, category: 'Views' },
  { id: 'view-swimlanes', module: 'kanban', label: 'Swimlanes (by assignee)', defaultKeys: { mac: '6', win: '6' }, matchKey: '6', scope: 'board', rebindable: true, category: 'Views' },
  { id: 'multi-select', module: 'kanban', label: 'Select multiple cards (then drag to move / nest)', defaultKeys: { mac: '⌘/⇧ + click', win: 'Ctrl/Shift + click' }, scope: 'board', rebindable: false, category: 'Actions' },
  { id: 'undo', module: 'kanban', label: 'Undo last action', defaultKeys: { mac: '⌘ Z', win: 'Ctrl + Z' }, scope: 'typing-safe', rebindable: false, category: 'Actions' },
  { id: 'redo', module: 'kanban', label: 'Redo', defaultKeys: { mac: '⇧⌘ Z', win: 'Shift + Ctrl + Z' }, scope: 'typing-safe', rebindable: false, category: 'Actions' },
  { id: 'cheat-sheet', module: 'kanban', label: 'Toggle this cheat sheet', defaultKeys: { mac: '?', win: '?' }, matchKey: '?', scope: 'board', rebindable: false, category: 'Actions' },
  { id: 'close', module: 'kanban', label: 'Close drawer / popover / cheat sheet', defaultKeys: { mac: 'Esc', win: 'Esc' }, matchKey: 'Escape', scope: 'board', rebindable: false, category: 'Actions' },
] as const

/** Keys no rebind may claim. This shrank in WP1: keys held by fixed-semantics
 *  TABLE rows ('/', '?', Escape) are guarded by findShortcutConflict via
 *  effectiveKey, so only keys with NO table row carrying a matchKey need
 *  explicit reservation — the undo/redo chord letters. */
export const RESERVED_KEYS: ReadonlySet<string> = new Set(['z', 'Z', 'Escape'])

/** operator override map: shortcut id -> exact KeyboardEvent.key. */
export type ShortcutOverrides = Record<string, string>

export const SHORTCUT_OVERRIDES_STORAGE_KEY = 'kanban:shortcut-overrides:v1'
/** Same-tab change signal (localStorage 'storage' events are cross-tab only). */
export const SHORTCUT_OVERRIDES_EVENT = 'kanban:shortcut-overrides-changed'

/** f(untrusted blob) -> a valid override map. Shared by the localStorage
 *  load AND the durable server tier (overridesSync.ts) so both sources
 *  validate identically: unknown ids, non-rebindable ids and non-string
 *  keys are dropped. */
export function sanitizeShortcutOverrides(raw: unknown): ShortcutOverrides {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const clean: ShortcutOverrides = {}
  for (const [id, key] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof key === 'string' && key.length > 0 && findShortcut(id)?.rebindable) {
      clean[id] = key
    }
  }
  return clean
}

export function loadShortcutOverrides(): ShortcutOverrides {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(SHORTCUT_OVERRIDES_STORAGE_KEY)
    return sanitizeShortcutOverrides(raw ? JSON.parse(raw) : {})
  } catch {
    return {}
  }
}

export function saveShortcutOverrides(next: ShortcutOverrides): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(SHORTCUT_OVERRIDES_STORAGE_KEY, JSON.stringify(next))
  window.dispatchEvent(new CustomEvent(SHORTCUT_OVERRIDES_EVENT))
}

/** f(callback) -> unsubscribe. Fires on same-tab saves AND cross-tab
 *  storage events, always with a freshly-loaded override map. */
export function subscribeShortcutOverrides(
  cb: (overrides: ShortcutOverrides) => void,
): () => void {
  if (typeof window === 'undefined') return () => {}
  const emit = () => cb(loadShortcutOverrides())
  const onStorage = (e: StorageEvent) => {
    if (e.key === SHORTCUT_OVERRIDES_STORAGE_KEY) emit()
  }
  window.addEventListener(SHORTCUT_OVERRIDES_EVENT, emit)
  window.addEventListener('storage', onStorage)
  return () => {
    window.removeEventListener(SHORTCUT_OVERRIDES_EVENT, emit)
    window.removeEventListener('storage', onStorage)
  }
}

export function findShortcut(id: string): ShortcutDef | undefined {
  return KANBAN_SHORTCUTS.find((s) => s.id === id)
}

/** f(def, overrides) -> the effective KeyboardEvent.key for a single-key
 *  action (override wins; falls back to the default matchKey). */
export function effectiveKey(def: ShortcutDef, overrides: ShortcutOverrides): string | undefined {
  if (def.rebindable && overrides[def.id]) return overrides[def.id]
  return def.matchKey
}

/**
 * f(candidateKey, forId, overrides) -> the shortcut currently holding that
 * key (or a 'reserved' marker), null when the key is free. Conflict rules:
 * reserved keys are never claimable; two actions can't share an effective
 * key regardless of scope (predictability beats cleverness).
 */
export function findShortcutConflict(
  candidateKey: string,
  forId: string,
  overrides: ShortcutOverrides,
): { kind: 'reserved' } | { kind: 'shortcut'; def: ShortcutDef } | null {
  if (RESERVED_KEYS.has(candidateKey)) return { kind: 'reserved' }
  for (const def of KANBAN_SHORTCUTS) {
    if (def.id === forId) continue
    if (effectiveKey(def, overrides) === candidateKey) return { kind: 'shortcut', def }
  }
  return null
}

/**
 * BUG-2026-07-06-KANBAN-C-HOTKEY-CASE — case-fold a pressed letter DOWN when
 * no shortcut actually claims the uppercase variant.
 *
 * `e.key` is case-sensitive: CapsLock or Shift turns 'c' into 'C', which
 * matched NOTHING and silently no-oped — while the cheat sheet advertises the
 * binding as "C" (display convention), actively teaching operators the
 * casing that didn't work. Operator report: "c doesn't work for archive".
 *
 * We can't blanket-lowercase: 'F' (Shift+F → toggle filters) is a REAL
 * distinct binding, and operators may rebind other uppercase chords. So the
 * fold only applies when the uppercase key is unclaimed — claimed uppercase
 * keys keep their exact-match semantics.
 *
 * f(pressedKey, overrides) -> pressedKey, lowercased iff it's a single
 * A-Z letter that no shortcut's effective key claims.
 */
export function foldUnclaimedUppercaseKey(
  pressedKey: string,
  overrides: ShortcutOverrides,
): string {
  if (pressedKey.length !== 1 || !/[A-Z]/.test(pressedKey)) return pressedKey
  const claimed =
    KANBAN_SHORTCUTS.some((def) => effectiveKey(def, overrides) === pressedKey) ||
    // Belt-and-braces: any raw override value counts as a claim even if its
    // def lookup ever drifts from effectiveKey's view of the table.
    Object.values(overrides).includes(pressedKey)
  return claimed ? pressedKey : pressedKey.toLowerCase()
}

/**
 * f(pressedKey, overrides) -> the CANONICAL default key the keydown switch
 * should process, preserving the switch's pinned shape:
 *   - pressed key is a custom binding for action X  → X's default matchKey
 *   - pressed key is a default that was rebound away → null (suppressed)
 *   - anything else → the pressed key, unchanged
 */
export function resolveCanonicalKey(
  pressedKey: string,
  overrides: ShortcutOverrides,
): string | null {
  for (const [id, key] of Object.entries(overrides)) {
    if (key !== pressedKey) continue
    const def = findShortcut(id)
    if (def?.rebindable && def.matchKey) return def.matchKey
  }
  for (const def of KANBAN_SHORTCUTS) {
    if (!def.rebindable || !def.matchKey) continue
    if (def.matchKey === pressedKey && overrides[def.id]) return null
  }
  return pressedKey
}

/** f() -> 'mac' | 'win' for defaultKeys display. */
export function getShortcutPlatform(): 'mac' | 'win' {
  if (typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform ?? '')) {
    return 'mac'
  }
  return 'win'
}

/** f(def, overrides, platform) -> the cheat-sheet display string. */
export function displayKey(
  def: ShortcutDef,
  overrides: ShortcutOverrides,
  platform: 'mac' | 'win' = getShortcutPlatform(),
): string {
  const custom = def.rebindable ? overrides[def.id] : undefined
  if (custom) {
    if (custom.length !== 1) return custom
    // e.key is case-sensitive: a captured UPPERCASE letter means the operator
    // held Shift ('G' = Shift+g). Show the chord explicitly — otherwise both
    // 'g' and 'G' would render as "G" and the operator couldn't tell which
    // binding they own (WP2 display disambiguation).
    if (/[A-Z]/.test(custom)) return platform === 'mac' ? `⇧ ${custom}` : `Shift + ${custom}`
    return custom.toUpperCase()
  }
  return def.defaultKeys[platform]
}

/** What a captured keypress may become. WP1/WP2 contract:
 *  - `ok`          — a bindable key (single printable char; Shift-chords
 *                    arrive naturally as their shifted character, e.g.
 *                    Shift+G = 'G', Shift+1 = '!', so they are bindable
 *                    with NO chord machinery).
 *  - `modifier`    — Cmd/Ctrl/Alt combos are deliberately NOT bindable:
 *                    the board keydown handler early-returns on them so
 *                    browser/OS shortcuts (cmd+F, ctrl+S…) are never
 *                    shadowed. Surfaced to the operator, not swallowed.
 *  - `nonprintable`— F-keys, arrows, Tab, etc. — no stable single-char
 *                    identity across platforms/layouts. */
export type RebindCaptureVerdict = 'ok' | 'modifier' | 'nonprintable'

/** f(keyboard event shape) -> verdict. Pure, so the capture policy is
 *  unit-testable without a DOM. The rebind UI turns non-`ok` verdicts
 *  into visible inline feedback (WP1: silence was the bug). */
export function classifyRebindCapture(e: {
  key: string
  metaKey: boolean
  ctrlKey: boolean
  altKey: boolean
}): RebindCaptureVerdict {
  if (e.metaKey || e.ctrlKey || e.altKey) return 'modifier'
  if (e.key.length !== 1) return 'nonprintable'
  return 'ok'
}
